import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Settings, Link2, Copy, Check, Loader2, RefreshCw, KeyRound } from 'lucide-react';

const IntegrationSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrations/hr/config`);
      setConfig(res.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching config:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleCreateIntegration = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/integrations/hr/config`, {});
      setConfig(res.data);
      toast.success('Integração criada com sucesso!');
      fetchConfig();
    } catch (error) {
      toast.error('Erro ao criar integração');
    } finally {
      setCreating(false);
    }
  };

  const handleRegenerateKey = async () => {
    if (!window.confirm('Regenerar a chave invalidará a chave atual. Deseja continuar?')) return;
    
    try {
      const res = await axios.post(`${API}/integrations/hr/regenerate-key`, {});
      setConfig({ ...config, api_key: res.data.api_key });
      toast.success('Nova chave gerada!');
    } catch (error) {
      toast.error('Erro ao regenerar chave');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const apiBaseUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Integração com RH
        </h1>
        <p className="text-slate-600 mt-1">Configure a sincronização automática com seu sistema de RH</p>
      </div>

      {!config ? (
        <Card className="border-slate-100">
          <CardContent className="p-12 text-center">
            <Link2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhuma integração configurada</h3>
            <p className="text-slate-500 mb-6">
              Crie uma integração para sincronizar colaboradores automaticamente com seu sistema de RH.
            </p>
            <Button 
              onClick={handleCreateIntegration}
              disabled={creating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" />
              )}
              Criar Integração
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* API Key Card */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <KeyRound className="w-5 h-5 text-blue-600" />
                Chave de API
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-500 mb-2">Use esta chave no header X-API-Key das requisições:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-slate-900 text-emerald-400 px-4 py-3 rounded-lg font-mono text-sm overflow-x-auto">
                    {config.api_key}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(config.api_key)}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={handleRegenerateKey}
                className="text-orange-600 border-orange-200 hover:bg-orange-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerar Chave
              </Button>
            </CardContent>
          </Card>

          {/* Endpoints Card */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <Settings className="w-5 h-5 text-blue-600" />
                Endpoints da API
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-bold">POST</span>
                    <span className="font-medium text-slate-900">Sincronizar Colaboradores em Massa</span>
                  </div>
                  <code className="text-sm text-slate-600 block bg-white px-3 py-2 rounded border">
                    {apiBaseUrl}/api/integrations/hr/sync
                  </code>
                  <p className="text-xs text-slate-500 mt-2">
                    Envie um array de colaboradores para criar/atualizar em massa
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-bold">POST</span>
                    <span className="font-medium text-slate-900">Webhook de Eventos</span>
                  </div>
                  <code className="text-sm text-slate-600 block bg-white px-3 py-2 rounded border">
                    {apiBaseUrl}/api/integrations/hr/webhook
                  </code>
                  <p className="text-xs text-slate-500 mt-2">
                    Receba eventos: employee.created, employee.updated, employee.terminated
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">GET</span>
                    <span className="font-medium text-slate-900">Listar Colaboradores</span>
                  </div>
                  <code className="text-sm text-slate-600 block bg-white px-3 py-2 rounded border">
                    {apiBaseUrl}/api/integrations/hr/employees
                  </code>
                  <p className="text-xs text-slate-500 mt-2">
                    Retorna todos os colaboradores cadastrados
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Example Request Card */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Exemplo de Requisição
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-900 rounded-xl p-4 overflow-x-auto">
                <pre className="text-sm text-slate-300 font-mono whitespace-pre-wrap">
{`curl -X POST "${apiBaseUrl}/api/integrations/hr/sync" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${config.api_key}" \\
  -d '{
    "employees": [
      {
        "external_id": "EMP001",
        "name": "João Silva",
        "email": "joao@empresa.com",
        "cpf": "123.456.789-00",
        "birth_date": "15/03/1990",
        "department": "TI",
        "is_active": true
      }
    ]
  }'`}
                </pre>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => copyToClipboard(`curl -X POST "${apiBaseUrl}/api/integrations/hr/sync" -H "Content-Type: application/json" -H "X-API-Key: ${config.api_key}" -d '{"employees": [{"external_id": "EMP001", "name": "João Silva", "email": "joao@empresa.com", "cpf": "123.456.789-00", "birth_date": "15/03/1990", "department": "TI", "is_active": true}]}'`)}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar Exemplo
              </Button>
            </CardContent>
          </Card>

          {/* Last Sync Info */}
          {config.last_sync && (
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-4 flex items-center gap-3">
                <Check className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="font-medium text-emerald-900">Última sincronização</p>
                  <p className="text-sm text-emerald-700">
                    {new Date(config.last_sync).toLocaleString('pt-BR')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// Message History Component

export default IntegrationSettings;
